import { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import API from "../api";
import { FiPrinter } from "react-icons/fi"; // Import Print Icon
import { FaFileExcel, FaCheck, FaTimes } from "react-icons/fa"; // Import Excel Icon
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md"; // Import checkbox icons
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const GeneralLedgerComponent = ({
  companyAddress,
  companyName,
  email,
  phone,
}) => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [ledgerData, setLedgerData] = useState({
    transactions: [],
    opening_debit: 0,
    opening_credit: 0,
    opening_balance: 0,
    closing_debit: 0,
    closing_credit: 0,
    closing_balance: 0,
    account_type: '',
    is_temporary_account: false,
    transaction_count: 0
  });
  const [loading, setLoading] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [journalEntries, setJournalEntries] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [selectedAccountsForExport, setSelectedAccountsForExport] = useState(
    {}
  );
  const glRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    startDate: "2024-01-01",
    endDate: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (selectedAccount) {
      fetchTransactions(selectedAccount.id);
    }
  }, [selectedAccount, filters]);

  const fetchAccounts = async () => {
    try {
      const res = await API.get("/accounts");
      const data = await res.data;
      setAccounts(data);

      const initialSelection = {};
      data.forEach((account) => {
        initialSelection[account.id] = true;
      });
      setSelectedAccountsForExport(initialSelection);

      const journalRes = await API.get("/oldest-entry-date");
      const oldestDate = journalRes.data?.oldest_date;
      if (oldestDate) {
        setFilters((prev) => ({
          ...prev,
          startDate: oldestDate,
        }));
      }
    } catch (error) {
      console.error("Error fetching accounts:", error);
    }
  };

  const handleRowClick = async (row) => {
    if (!row.id || row.is_opening_balance) return; // Don't open modal for opening balance rows
    
    setSelectedTransaction(row);
    setModalOpen(true);

    try {
      const res = await API.get(`/journal_entry/${row.id}`);
      setJournalEntries(res.data.lines);
    } catch (error) {
      toast.error("Error fetching journal entries.");
      console.error("Error fetching journal entries:", error);
    }
  };

  const fetchTransactions = async (accountId) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        accountId,
        startDate: filters.startDate,
        endDate: filters.endDate,
      }).toString();

      const res = await API.get(`/ledger?${queryParams}`);
      const data = await res.data;
      
      // Check if we have the new structure with metadata
      if (data.transactions !== undefined) {
        setLedgerData({
          transactions: data.transactions || [],
          opening_debit: data.opening_debit || 0,
          opening_credit: data.opening_credit || 0,
          opening_balance: data.opening_balance || 0,
          closing_debit: data.closing_debit || 0,
          closing_credit: data.closing_credit || 0,
          closing_balance: data.closing_balance || 0,
          account_type: data.account_type || '',
          is_temporary_account: data.is_temporary_account || false,
          transaction_count: data.transaction_count || 0
        });
      } else {
        // Fallback for old structure
        setLedgerData({
          transactions: data || [],
          opening_debit: 0,
          opening_credit: 0,
          opening_balance: 0,
          closing_debit: 0,
          closing_credit: 0,
          closing_balance: 0,
          account_type: selectedAccount?.account_type || '',
          is_temporary_account: false,
          transaction_count: (data || []).length
        });
      }
    } catch (error) {
      toast.error("Error fetching transactions.");
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAccounts = accounts.filter((account) =>
    account.account_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    {
      name: "Date",
      selector: (row) => {
        if (row.is_opening_balance) {
          return "Opening Balance";
        }
        return format(new Date(row.date), "dd MM yyyy");
      },
      sortable: true,
    },
    {
      name: "Description",
      selector: (row) => {
        if (row.is_opening_balance) {
          return "Balance Brought Forward";
        }
        return row.description || "N/A";
      },
      grow: 2,
      style: {
        whiteSpace: "normal",
        wordBreak: "break-word",
      },
    },
    {
      name: "Debit",
      selector: (row) => {
        const debit = row.debit || 0;
        return debit > 0 ? debit.toFixed(2) : "";
      },
      right: true,
    },
    {
      name: "Credit",
      selector: (row) => {
        const credit = row.credit || 0;
        return credit > 0 ? credit.toFixed(2) : "";
      },
      right: true,
    },
    {
      name: "Balance",
      selector: (row) => (row.balance || 0).toFixed(2),
      right: true,
      conditionalCellStyles: [
        {
          when: (row) => row.balance > 0 && (ledgerData.account_type === 'Asset' || ledgerData.account_type === 'Expense'),
          style: {
            color: 'green',
            fontWeight: 'bold',
          },
        },
        {
          when: (row) => row.balance < 0,
          style: {
            color: 'red',
            fontWeight: 'bold',
          },
        },
      ],
    },
  ];

  const toggleAccountSelection = (accountId) => {
    setSelectedAccountsForExport((prev) => ({
      ...prev,
      [accountId]: !prev[accountId],
    }));
  };

  const selectAllAccounts = () => {
    const allSelected = {};
    accounts.forEach((account) => {
      allSelected[account.id] = true;
    });
    setSelectedAccountsForExport(allSelected);
  };

  const deselectAllAccounts = () => {
    const noneSelected = {};
    accounts.forEach((account) => {
      noneSelected[account.id] = false;
    });
    setSelectedAccountsForExport(noneSelected);
  };

  const getSelectedAccountIds = () => {
    return Object.keys(selectedAccountsForExport).filter(
      (id) => selectedAccountsForExport[id]
    );
  };

  const safeToFixed = (value, decimals = 2) => {
    const num = parseFloat(value);
    if (isNaN(num)) return "0.00";
    return num.toFixed(decimals);
  };

  // 🔵 EXPORT SELECTED ACCOUNTS TO EXCEL FUNCTION
  const exportSelectedAccountsToExcel = async () => {
    const selectedAccountIds = getSelectedAccountIds();

    if (selectedAccountIds.length === 0) {
      toast.warning("Please select at least one account to export.");
      return;
    }

    setExporting(true);

    try {
      const workbook = XLSX.utils.book_new();
      let hasData = false;

      // Export summary sheet
      const summaryData = [
        ["GENERAL LEDGER EXPORT REPORT"],
        [`Company: ${companyName || "N/A"}`],
        [`Address: ${companyAddress || "N/A"}`],
        [`Report Period: ${filters.startDate} to ${filters.endDate}`],
        [`Generated: ${new Date().toLocaleDateString()}`],
        [``],
        ["SELECTED ACCOUNTS FOR EXPORT:"],
        ["Account Name", "Account Type", "Account Class"],
      ];

      const selectedAccounts = accounts.filter(
        (account) => selectedAccountsForExport[account.id]
      );

      selectedAccounts.forEach((account) => {
        const isTemp = account.account_type === 'Expense' || account.account_type === 'Revenue';
        summaryData.push([
          account.account_name, 
          account.account_type || "N/A",
          isTemp ? "Temporary (P&L)" : "Permanent (Balance Sheet)"
        ]);
      });

      summaryData.push([""]);
      summaryData.push([
        "Total Accounts Exported:",
        selectedAccounts.length.toString(),
        "",
      ]);

      const summaryWorksheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(
        workbook,
        summaryWorksheet,
        "Export Summary"
      );

      // Export each selected account's transactions
      for (const account of selectedAccounts) {
        const queryParams = new URLSearchParams({
          accountId: account.id,
          startDate: filters.startDate,
          endDate: filters.endDate,
        }).toString();

        const res = await API.get(`/ledger?${queryParams}`);
        const ledgerResponse = res.data;
        const transactions = ledgerResponse.transactions || [];

        if (transactions.length > 0) {
          hasData = true;

          // Determine if this is a temporary account
          const isTemporary = account.account_type === 'Expense' || account.account_type === 'Revenue';
          
          // Prepare data for this account sheet
          const sheetData = [
            [`Account: ${account.account_name}`],
            [`Account Type: ${account.account_type || "N/A"} (${isTemporary ? "Temporary" : "Permanent"})`],
            [`Report Period: ${filters.startDate} to ${filters.endDate}`],
            [""],
            ["Opening Balance Details:"],
            [`Debit: ${safeToFixed(ledgerResponse.opening_debit || 0)}`],
            [`Credit: ${safeToFixed(ledgerResponse.opening_credit || 0)}`],
            [`Net Opening Balance: ${safeToFixed(ledgerResponse.opening_balance || 0)}`],
            [""],
            ["TRANSACTIONS"],
            ["Date", "Description", "Debit", "Credit", "Balance"],
          ];

          transactions.forEach((transaction) => {
            sheetData.push([
              transaction.is_opening_balance 
                ? "Opening" 
                : format(new Date(transaction.date), "dd-MM-yyyy"),
              transaction.description || (transaction.is_opening_balance ? "Balance Brought Forward" : "N/A"),
              (transaction.debit || 0) > 0 ? safeToFixed(transaction.debit) : "",
              (transaction.credit || 0) > 0 ? safeToFixed(transaction.credit) : "",
              safeToFixed(transaction.balance || 0),
            ]);
          });

          // Add totals and closing balance
          sheetData.push([""]);
          sheetData.push([
            "",
            "TOTALS:",
            safeToFixed(ledgerResponse.closing_debit || 0),
            safeToFixed(ledgerResponse.closing_credit || 0),
            "",
          ]);
          sheetData.push([
            "",
            "CLOSING BALANCE:",
            "",
            "",
            safeToFixed(ledgerResponse.closing_balance || 0),
          ]);

          // Sanitize sheet name
          let sheetName = account.account_name.replace(/[:\\/\?\*\[\]]/g, "");
          sheetName =
            sheetName.length > 30
              ? sheetName.substring(0, 27) + "..."
              : sheetName;

          const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

          // Apply styling
          const range = XLSX.utils.decode_range(worksheet["!ref"]);
          for (let row = range.s.r; row <= range.e.r; row++) {
            for (let col = range.s.c; col <= range.e.c; col++) {
              const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
              if (!worksheet[cellAddress]) continue;

              // Format header rows
              if (row < 10) {
                worksheet[cellAddress].s = { font: { bold: true } };
              }

              // Format column headers (row 10)
              if (row === 10) {
                worksheet[cellAddress].s = {
                  font: { bold: true },
                  fill: { fgColor: { rgb: "E0E0E0" } },
                };
              }

              // Format totals and closing balance rows
              const totalRows = [sheetData.length - 2, sheetData.length - 3, sheetData.length - 1];
              if (totalRows.includes(row)) {
                worksheet[cellAddress].s = { 
                  font: { bold: true },
                  fill: { fgColor: { rgb: "F0F0F0" } }
                };
              }

              // Format numeric columns (C, D, E) - Debit, Credit, Balance
              if (col >= 2 && col <= 4 && sheetData[row]?.[col]) {
                worksheet[cellAddress].s = {
                  numFmt: "#,##0.00",
                  alignment: { horizontal: "right" },
                };
              }
            }
          }

          // Set column widths
          worksheet["!cols"] = [
            { wch: 12 }, // Date
            { wch: 40 }, // Description
            { wch: 15 }, // Debit
            { wch: 15 }, // Credit
            { wch: 15 }, // Balance
          ];

          XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
        }
      }

      if (!hasData) {
        toast.info("No transaction data found for the selected accounts.");
        setExporting(false);
        return;
      }

      // Save the Excel file
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const data = new Blob([excelBuffer], {
        type: "application/octet-stream",
      });

      const fileName = `General_Ledger_${filters.startDate}_to_${filters.endDate}.xlsx`;
      saveAs(data, fileName);

      toast.success(
        `Successfully exported ${selectedAccounts.length} account(s) to Excel.`
      );
      setExportModalOpen(false);
    } catch (error) {
      toast.error("Error exporting transactions to Excel.");
      console.error("Error exporting transactions:", error);
    } finally {
      setExporting(false);
    }
  };

  // 🔵 EXPORT CURRENT ACCOUNT TO EXCEL FUNCTION
  const exportCurrentAccountToExcel = () => {
    if (!selectedAccount) {
      toast.warning("Please select an account first.");
      return;
    }

    if (ledgerData.transactions.length === 0) {
      toast.info("No transactions to export.");
      return;
    }

    // Prepare sheet data with metadata
    const sheetData = [
      [`Account: ${selectedAccount.account_name}`],
      [`Account Type: ${ledgerData.account_type || selectedAccount.account_type} (${ledgerData.is_temporary_account ? 'Temporary' : 'Permanent'})`],
      [`Report Period: ${filters.startDate} to ${filters.endDate}`],
      [`Generated: ${new Date().toLocaleDateString()}`],
      [""],
      ["Opening Balance Details:"],
      [`Debit: ${safeToFixed(ledgerData.opening_debit)}`],
      [`Credit: ${safeToFixed(ledgerData.opening_credit)}`],
      [`Net Opening Balance: ${safeToFixed(ledgerData.opening_balance)}`],
      [""],
      ["TRANSACTIONS"],
      ["Date", "Description", "Debit", "Credit", "Balance"],
    ];

    ledgerData.transactions.forEach((transaction) => {
      sheetData.push([
        transaction.is_opening_balance 
          ? "Opening" 
          : format(new Date(transaction.date), "dd-MM-yyyy"),
        transaction.description || (transaction.is_opening_balance ? "Balance Brought Forward" : "N/A"),
        (transaction.debit || 0) > 0 ? safeToFixed(transaction.debit) : "",
        (transaction.credit || 0) > 0 ? safeToFixed(transaction.credit) : "",
        safeToFixed(transaction.balance || 0),
      ]);
    });

    // Add totals and closing balance
    sheetData.push([""]);
    sheetData.push([
      "",
      "TOTALS:",
      safeToFixed(ledgerData.closing_debit),
      safeToFixed(ledgerData.closing_credit),
      "",
    ]);
    sheetData.push([
      "",
      "CLOSING BALANCE:",
      "",
      "",
      safeToFixed(ledgerData.closing_balance),
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    
    // Sanitize sheet name
    let sheetName = selectedAccount.account_name.replace(/[:\\/\?\*\[\]]/g, "");
    sheetName = sheetName.length > 30 ? sheetName.substring(0, 27) + "..." : sheetName;
    
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });

    const fileName = `GL_${selectedAccount.account_name}_${filters.startDate}_to_${filters.endDate}.xlsx`;
    saveAs(data, fileName);
  };

  const handlePrint = async () => {
    // ... (your existing print function)
  };

  // Calculate summary for display
  const getSummaryText = () => {
    if (!selectedAccount) return null;
    
    const isTemporary = ledgerData.is_temporary_account;
    const accountType = ledgerData.account_type || selectedAccount.account_type;
    
    return (
      <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="font-semibold">Account Type:</span>
            <span className={`ml-2 ${isTemporary ? 'text-orange-600' : 'text-blue-600'}`}>
              {accountType} ({isTemporary ? 'Temporary' : 'Permanent'})
            </span>
          </div>
          <div>
            <span className="font-semibold">Opening Balance:</span>
            <span className="ml-2">
              {ledgerData.opening_balance >= 0 ? 'Dr' : 'Cr'} {Math.abs(ledgerData.opening_balance).toFixed(2)}
            </span>
          </div>
          <div>
            <span className="font-semibold">Closing Balance:</span>
            <span className={`ml-2 ${ledgerData.closing_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {ledgerData.closing_balance >= 0 ? 'Dr' : 'Cr'} {Math.abs(ledgerData.closing_balance).toFixed(2)}
            </span>
          </div>
          <div>
            <span className="font-semibold">Transactions:</span>
            <span className="ml-2">{ledgerData.transaction_count}</span>
          </div>
        </div>
        {isTemporary && ledgerData.opening_balance === 0 && (
          <div className="mt-2 text-xs text-gray-500 italic">
            Note: This is a temporary account (Income Statement). Opening balance is typically zero.
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex max-h-[calc(100vh-100px)] overflow-y-scroll bg-gray-100">
      {/* Sidebar - Accounts List */}
      <aside className="w-1/4 bg-white border-r shadow-md p-4 flex flex-col">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Accounts</h3>
        <input
          type="text"
          placeholder="Search accounts..."
          className="border rounded-md p-2 mb-3 w-full focus:ring focus:ring-blue-200"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <ul className="overflow-y-auto flex-grow">
          {filteredAccounts.length > 0 ? (
            filteredAccounts.map((account) => {
              const isTemporary = account.account_type === 'Expense' || account.account_type === 'Revenue';
              return (
                <li
                  key={account.id}
                  className={`p-2 cursor-pointer rounded-md transition flex items-center ${
                    selectedAccount?.id === account.id
                      ? isTemporary ? "bg-orange-600 text-white" : "bg-blue-600 text-white"
                      : "hover:bg-gray-200"
                  }`}
                  onClick={() => setSelectedAccount(account)}
                >
                  <span className="flex-grow">{account.account_name}</span>
                  <span className={`text-xs ${selectedAccount?.id === account.id ? 'text-white' : 'text-gray-500'} ml-2`}>
                    {account.account_type}
                    {isTemporary && (
                      <span className="ml-1 text-xs opacity-75">(Temp)</span>
                    )}
                  </span>
                </li>
              );
            })
          ) : (
            <p className="text-gray-500 text-sm">No accounts found.</p>
          )}
        </ul>
      </aside>

      {/* Main Content */}
      <div ref={glRef} className="w-3/4 p-6 bg-white shadow-lg rounded-lg">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">General Ledger</h2>
          <div className="flex space-x-4 items-center">
            {/* From Date */}
            <label className="text-gray-700 font-medium">From:</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) =>
                setFilters({ ...filters, startDate: e.target.value })
              }
              className="border rounded-md p-2 focus:ring focus:ring-blue-200"
            />

            {/* To Date */}
            <label className="text-gray-700 font-medium">To:</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) =>
                setFilters({ ...filters, endDate: e.target.value })
              }
              className="border rounded-md p-2 focus:ring focus:ring-blue-200"
            />

            {/* Export Selection Modal Trigger */}
            <button
              onClick={() => setExportModalOpen(true)}
              className="flex items-center gap-2 bg-green-600 text-white py-2 px-4 text-sm rounded-md hover:bg-green-700 transition"
            >
              <FaFileExcel className="text-lg" /> Select & Export
            </button>

            {/* Export Current Account Button */}
            <button
              onClick={exportCurrentAccountToExcel}
              className="flex items-center gap-2 bg-blue-600 text-white py-2 px-4 text-sm rounded-md hover:bg-blue-700 transition"
              disabled={!selectedAccount}
            >
              <FaFileExcel className="text-lg" /> Export Current
            </button>
          </div>
        </div>

        {/* Account Summary */}
        {selectedAccount && getSummaryText()}

        {/* Export Selection Modal */}
        {exportModalOpen && (
          <div className="fixed inset-0 flex z-50 items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-4/5 max-h-[80vh] flex flex-col">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Select Accounts to Export</h3>
                <div className="flex justify-end gap-3 border-t pt-4">
                  <button
                    onClick={() => setExportModalOpen(false)}
                    className="px-4 py-2 text-gray-700 border rounded-md hover:bg-gray-100 transition"
                    disabled={exporting}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={exportSelectedAccountsToExcel}
                    disabled={exporting}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md transition ${
                      exporting
                        ? "bg-green-400 cursor-not-allowed"
                        : "bg-green-600 hover:bg-green-700"
                    } text-white`}
                  >
                    {exporting ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span>Exporting...</span>
                      </>
                    ) : (
                      <>
                        <FaFileExcel />
                        <span>Export Selected Accounts</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="flex gap-4 mb-4">
                <button
                  onClick={selectAllAccounts}
                  disabled={exporting}
                  className={`flex items-center gap-2 py-2 px-4 rounded-md transition ${
                    exporting
                      ? "bg-blue-50 text-blue-400 cursor-not-allowed"
                      : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                  }`}
                >
                  <MdCheckBox /> Select All
                </button>
                <button
                  onClick={deselectAllAccounts}
                  disabled={exporting}
                  className={`flex items-center gap-2 py-2 px-4 rounded-md transition ${
                    exporting
                      ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <MdCheckBoxOutlineBlank /> Deselect All
                </button>
                <div className="ml-auto">
                  <span className="text-sm text-gray-600">
                    Selected: {getSelectedAccountIds().length} of{" "}
                    {accounts.length} accounts
                  </span>
                </div>
              </div>

              <div className="flex-grow overflow-y-auto mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {accounts.map((account) => {
                    const isTemporary = account.account_type === 'Expense' || account.account_type === 'Revenue';
                    return (
                      <div
                        key={account.id}
                        className={`flex items-center p-3 border rounded-lg transition ${
                          exporting
                            ? "cursor-not-allowed opacity-70"
                            : "cursor-pointer hover:bg-gray-100"
                        } ${
                          selectedAccountsForExport[account.id]
                            ? isTemporary ? "bg-orange-50 border-orange-300" : "bg-green-50 border-green-300"
                            : "bg-gray-50 border-gray-200"
                        }`}
                        onClick={() =>
                          !exporting && toggleAccountSelection(account.id)
                        }
                      >
                        <div className="mr-3">
                          {selectedAccountsForExport[account.id] ? (
                            <MdCheckBox className={`${isTemporary ? 'text-orange-600' : 'text-green-600'}`} size={20} />
                          ) : (
                            <MdCheckBoxOutlineBlank
                              className="text-gray-400"
                              size={20}
                            />
                          )}
                        </div>
                        <div className="flex-grow">
                          <div className="font-medium">
                            {account.account_name}
                          </div>
                          <div className="text-xs flex justify-between">
                            <span className={`${isTemporary ? 'text-orange-600' : 'text-gray-500'}`}>
                              {account.account_type}
                            </span>
                            <span className="text-gray-400">
                              {isTemporary ? '(Temp)' : '(Perm)'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {modalOpen && (
          <div className="fixed inset-0 flex z-10 items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-3/4 max-h-[80vh] overflow-y-auto">
              <h3 className="text-xl font-bold mb-4">
                Journal Entries for {selectedTransaction?.description}
              </h3>

              {journalEntries.length > 0 ? (
                journalEntries.map((entry, index) => (
                  <div
                    key={index}
                    className="mb-6 p-4 border border-gray-300 rounded-lg shadow"
                  >
                    <h4 className="text-lg font-semibold text-gray-700">
                      Journal Entry #{entry.journal_entry_id} - {entry.date}
                    </h4>
                    <p className="text-gray-600">
                      Description: {entry.description}
                    </p>

                    <table className="w-full border-collapse border border-gray-300 mt-3">
                      <thead>
                        <tr className="bg-gray-200">
                          <th className="border border-gray-300 px-4 py-2 text-left">
                            Account
                          </th>
                          <th className="border border-gray-300 px-4 py-2 text-right">
                            Debit
                          </th>
                          <th className="border border-gray-300 px-4 py-2 text-right">
                            Credit
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {entry.accounts.map((acc, i) => (
                          <tr key={i} className="hover:bg-gray-100">
                            <td className="border border-gray-300 px-4 py-2">
                              {acc.account_name}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-right">
                              {acc.debit > 0 ? acc.debit.toFixed(2) : ""}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-right">
                              {acc.credit > 0 ? acc.credit.toFixed(2) : ""}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center">
                  No journal entries found.
                </p>
              )}

              <div className="flex justify-end mt-4">
                <button
                  className="bg-gray-500 text-white py-2 px-4 rounded"
                  onClick={() => setModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Transactions Table */}
        <div
          id="printable-table"
          className="border rounded-lg shadow-sm max-h-[calc(100vh-200px)] overflow-y-scroll"
        >
          <DataTable
            columns={columns}
            data={ledgerData.transactions}
            progressPending={loading}
            pagination
            highlightOnHover
            paginationRowsPerPageOptions={[
              10,
              20,
              50,
              100,
              ledgerData.transactions.length,
            ]}
            striped
            onRowClicked={(row) => handleRowClick(row)}
            pointerOnHover
            customStyles={{
              rows: {
                style: {
                  cursor: (row) => row.id && !row.is_opening_balance ? "pointer" : "default",
                  minHeight: "50px",
                  fontSize: "14px",
                  zIndex: 1,
                },
              },
              headCells: {
                style: {
                  backgroundColor: "#f3f4f6",
                  fontWeight: "bold",
                  fontSize: "15px",
                },
              },
              cells: {
                style: {
                  '&:first-of-type': {
                    fontWeight: (row) => row.is_opening_balance ? 'bold' : 'normal',
                  },
                },
              },
              pagination: { style: { fontSize: "14px" } },
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default GeneralLedgerComponent;